/**
 * Front behavior for sinthia/responsive-video
 * - PC/SP の動画ソースとアスペクト比を切り替え
 * - autoplay, loop, muted, controls を data-* から適用
 * - pauseWhenOut / autoload に応じて IntersectionObserver で制御
 * - オーバーレイ位置も PC/SP で切り替え
 */
(() => {
  const $all = document.querySelectorAll('.responsive-video');
  if (!$all.length) return;

  const toBool = (v) => v === '1' || v === 'true';
  const px = (el) => el.getBoundingClientRect().width;

  const anchorToTranslate = (anchor) => {
    switch (anchor) {
      case 'tl': return ['0%','0%'];
      case 'tc': return ['-50%','0%'];
      case 'tr': return ['-100%','0%'];
      case 'cl': return ['0%','-50%'];
      case 'cr': return ['-100%','-50%'];
      case 'bl': return ['0%','-100%'];
      case 'bc': return ['-50%','-100%'];
      case 'br': return ['-100%','-100%'];
      case 'center':
      default:   return ['-50%','-50%'];
    }
  };

  const ensureVideo = ($wrap) => {
    let $video = $wrap.querySelector('video');
    if (!$video) {
      $video = document.createElement('video');
      $video.preload = 'metadata';
      $video.setAttribute('playsinline', '');
      $video.style.width = '100%';
      $video.style.height = '100%';
      $wrap.innerHTML = ''; // poster/placeholder を消す
      $wrap.appendChild($video);
    }
    return $video;
  };

  const applyRatio = ($root) => {
    const bp = Number($root.dataset.ratioBp || 768);
    const ratioPc = $root.dataset.ratioPc || '';
    const ratioSp = $root.dataset.ratioSp || '';
    const width = px($root);
    const useSp = width < bp;
    const chosen = useSp ? ratioSp : ratioPc;
    const $media = $root.querySelector('.rvideo__media');
    if ($media) {
      if (chosen) {
        $media.style.aspectRatio = chosen;
      } else {
        $media.style.removeProperty('aspect-ratio');
      }
    }
  };

  const applySourceAndAttrs = ($root) => {
    const bp = Number($root.dataset.ratioBp || 768);
    const width = px($root);
    const useSp = width < bp;

    const srcPc = $root.dataset.pc || '';
    const srcSp = $root.dataset.sp || '';
    const src = useSp ? (srcSp || srcPc) : (srcPc || srcSp);

    const $media = $root.querySelector('.rvideo__media');
    if (!$media) return;

    const $video = ensureVideo($media);
    if ($video.dataset.bound !== '1') {
      // 初期属性
      $video.loop = toBool($root.dataset.loop || '0');
      $video.muted = toBool($root.dataset.autoplay || $root.dataset.muted || '1'); // 自動再生時は強制ミュート
      $video.controls = toBool($root.dataset.controls || '0');
      $video.playsInline = toBool($root.dataset.playsinline || '1');
      $video.style.objectFit = $root.dataset.objectfit || 'cover';
      $video.dataset.bound = '1';
    }

    if ($video.currentSrc !== src) {
      $video.src = src || '';
    }

    // ここでは再生までは行わない（実際の再生は IO 側で制御）
    $root._rvideoLoaded = true;
  };

  // オーバーレイ位置を PC/SP で切り替え
  const applyOverlay = ($root) => {
    const bp = Number($root.dataset.ratioBp || 768);
    const width = px($root);
    const useSp = width < bp;

    const xPc = $root.dataset.ovxPc || '50';
    const yPc = $root.dataset.ovyPc || '50';
    const aPc = $root.dataset.ovanchorPc || 'center';

    const xSp = $root.dataset.ovxSp || xPc;
    const ySp = $root.dataset.ovySp || yPc;
    const aSp = $root.dataset.ovanchorSp || aPc;

    const x = useSp ? xSp : xPc;
    const y = useSp ? ySp : yPc;
    const anchor = useSp ? aSp : aPc;

    const $slot = $root.querySelector('.rvideo__overlaySlot');
    if (!$slot) return;

    const [tx, ty] = anchorToTranslate(anchor);

    $slot.style.position = 'absolute';
    $slot.style.top = `${y}%`;
    $slot.style.left = `${x}%`;
    $slot.style.transform = `translate(${tx}, ${ty})`;
  };

  // poster を data-* から適用
  const applyPoster = ($root) => {
    const $poster = $root.querySelector('.rvideo__poster');
    if (!$poster) return;
    const url = $poster.dataset.poster;
    if (url) {
      $poster.style.backgroundImage = `url("${url}")`;
    } else {
      $poster.style.removeProperty('background-image');
    }
  };

  /**
   * IntersectionObserver
   * - autoload = true: 初期ロード後、pauseWhenOut だけ制御
   * - autoload = false: 画面に入るまで video/src を生成しない
   */
  const setupIO = ($root) => {
    const autoload = toBool($root.dataset.autoload || '0');
    const pauseWhenOut = toBool($root.dataset.pausewhenout || '0');
    const threshold = Math.min(Math.max(Number($root.dataset.threshold || 0.25), 0.01), 0.99);

    // autoload = true かつ pauseWhenOut 無効なら、IOは不要
    if (autoload && !pauseWhenOut) return;

    let hasLoaded = !!$root._rvideoLoaded;

    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const visible = entry.intersectionRatio >= threshold;

        // autoload=false の場合、初めて見えたタイミングで src を付ける
        if (!autoload && visible && !hasLoaded) {
          applySourceAndAttrs($root);
          hasLoaded = true;
        }

        const $video = $root.querySelector('video');
        if (!$video) return;

        const shouldAutoplay = toBool($root.dataset.autoplay || '0');

        if (visible) {
          if (shouldAutoplay) {
            $video.play().catch(() => {});
          }
          // autoload=false かつ pauseWhenOut=false の場合、
          // 一度ロードして可視状態になったら監視を外してもよい
          if (!autoload && !pauseWhenOut) {
            io.unobserve($root);
          }
        } else {
          if (pauseWhenOut && shouldAutoplay) {
            $video.pause();
          }
        }
      });
    }, { threshold: [threshold, 0, 1] });

    io.observe($root);
    $root._rvideoIO = io;
  };

  const mount = ($root) => {
    applyRatio($root);
    applyOverlay($root);
    applyPoster($root);

    const autoload = toBool($root.dataset.autoload || '0');

    if (autoload) {
      // 旧来どおり即ロード
      applySourceAndAttrs($root);

      // pauseWhenOut が有効なら再生/一時停止を制御
      setupIO($root);
    } else {
      // autoload=false: 画面に入るまで video/src は生成しない
      setupIO($root);
    }
  };

  const handleResize = () => {
    $all.forEach(($root) => {
      applyRatio($root);
      applyOverlay($root);
      applyPoster($root);

      const autoload = toBool($root.dataset.autoload || '0');
      const alreadyLoaded = !!$root._rvideoLoaded;

      // autoload=true は常にソースを選び直す
      if (autoload) {
        applySourceAndAttrs($root);
      } else if (alreadyLoaded) {
        // autoload=false でも一度ロード済みなら、ブレイクポイント変更時にソースを取り直す
        applySourceAndAttrs($root);
      }
    });
  };

  // 初期化
  $all.forEach(mount);
  window.addEventListener('resize', handleResize, { passive: true });
})();
